import React from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";

export interface FormField {
  id?: string;
  type: string;
  label: string;
  name: string;
  placeholder?: string;
  required?: boolean;
  options?: string[];
  queueField?: string; // Maps to queue item field
}

interface PublicFormFieldProps {
  field: FormField;
  value: any;
  onChange: (name: string, value: any) => void;
}

const PublicFormFieldComponent: React.FC<PublicFormFieldProps> = ({ field, value, onChange }) => {
  const fieldId = `field-${field.name}`;

  switch (field.type) {
    case 'text':
    case 'email':
    case 'tel':
    case 'number':
      return (
        <div className="space-y-2">
          <Label htmlFor={fieldId}>
            {field.label}
            {field.required && <span className="text-destructive ml-1">*</span>}
          </Label>
          <Input
            id={fieldId}
            type={field.type}
            placeholder={field.placeholder}
            autoComplete="on"
            value={value ?? ''}
            onChange={(e) => onChange(field.name, e.target.value)}
            required={field.required}
          />
        </div>
      );

    case 'textarea':
      return (
        <div className="space-y-2">
          <Label htmlFor={fieldId}>
            {field.label}
            {field.required && <span className="text-destructive ml-1">*</span>}
          </Label>
          <Textarea
            id={fieldId}
            placeholder={field.placeholder}
            autoComplete="on"
            value={value ?? ''}
            onChange={(e) => onChange(field.name, e.target.value)}
            required={field.required}
          />
        </div>
      );

    case 'select':
      return (
        <div className="space-y-2">
          <Label htmlFor={fieldId}>
            {field.label}
            {field.required && <span className="text-destructive ml-1">*</span>}
          </Label>
          <Select
            value={value ?? undefined}
            onValueChange={(val) => onChange(field.name, val)}
          >
            <SelectTrigger>
              <SelectValue placeholder={field.placeholder || "Select an option"} />
            </SelectTrigger>
            <SelectContent className="z-50 bg-background">
              {field.options?.filter(Boolean).map((option, index) => (
                <SelectItem key={index} value={option}>
                  {option}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      );

    case 'checkbox':
      return (
        <div className="space-y-3">
          <Label className="text-sm font-medium">
            {field.label}
            {field.required && <span className="text-destructive ml-1">*</span>}
          </Label>
          <div className="space-y-2">
            {field.options && field.options.length > 0 ? (
              field.options.filter(Boolean).map((option, optIndex) => {
                const optionId = `${fieldId}_${optIndex}`;
                const currentValues = Array.isArray(value) ? value : [];
                return (
                  <div key={optIndex} className="flex items-center space-x-2">
                    <Checkbox
                      id={optionId}
                      checked={currentValues.includes(option)}
                      onCheckedChange={(checked) => {
                        const newValues = checked
                          ? [...currentValues, option]
                          : currentValues.filter((v: string) => v !== option);
                        onChange(field.name, newValues);
                      }}
                    />
                    <Label htmlFor={optionId} className="text-sm cursor-pointer">
                      {option}
                    </Label>
                  </div>
                );
              })
            ) : (
              <div className="flex items-center space-x-2">
                <Checkbox
                  id={fieldId}
                  checked={Boolean(value)}
                  onCheckedChange={(checked) => onChange(field.name, checked)}
                  required={field.required}
                />
                <Label htmlFor={fieldId} className="text-sm cursor-pointer">
                  {field.placeholder || field.label}
                </Label>
              </div>
            )}
          </div>
        </div>
      );

    default:
      return null;
  }
};

export const PublicFormField = React.memo(PublicFormFieldComponent);
